import { memo, useEffect, useState } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { PageItem } from '../lib/types';
import { renderThumbnail } from '../lib/thumbnails';
import { ExpandIcon, RotateLeftIcon, RotateRightIcon, TrashIcon, ScissorsIcon } from './Icons';

interface PageCardProps {
  page: PageItem;
  /** Position in the working canvas, 1-based, for display. */
  position: number;
  selected: boolean;
  splitAfter: boolean;
  onToggleSelect: (uid: string, additive: boolean, range: boolean) => void;
  onRotate: (uid: string, delta: number) => void;
  onRemove: (uid: string) => void;
  onToggleSplit: (uid: string) => void;
  onPreview: (uid: string) => void;
}

function PageCardImpl({
  page,
  position,
  selected,
  splitAfter,
  onToggleSelect,
  onRotate,
  onRemove,
  onToggleSplit,
  onPreview,
}: PageCardProps) {
  const [thumb, setThumb] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: page.uid,
  });

  useEffect(() => {
    // A blank page has no source to render; it is drawn by CSS instead.
    if (page.blank) return;
    let cancelled = false;
    setFailed(false);
    renderThumbnail(page.docId, page.pageIndex)
      .then((url) => {
        if (!cancelled) setThumb(url);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [page.blank, page.docId, page.pageIndex]);

  const className = [
    'page-card',
    selected && 'page-card--selected',
    isDragging && 'page-card--dragging',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <>
      <div
        ref={setNodeRef}
        className={className}
        style={{ transform: CSS.Translate.toString(transform), transition }}
      >
        <input
          type="checkbox"
          className="page-card__check"
          checked={selected}
          // Selection is handled in onClick so it can read the modifier keys.
          // onChange exists only to keep this a controlled input; doing the work
          // in both would toggle twice per click and cancel itself out.
          // Keyboard activation still reaches onClick, which browsers synthesize.
          onChange={() => undefined}
          onClick={(event) => {
            event.stopPropagation();
            onToggleSelect(page.uid, event.metaKey || event.ctrlKey, event.shiftKey);
          }}
          aria-label={`Select page ${position}`}
        />
        <span className="page-card__index">{position}</span>
        {page.texts?.length ? (
          <span className="page-card__badge" title={`${page.texts.length} text box(es) added`}>
            T
          </span>
        ) : null}

        {/* Double-click matches how thumbnails behave everywhere else; the
            button in the footer is the discoverable route to the same thing. */}
        <div
          className="page-card__thumb"
          onDoubleClick={() => onPreview(page.uid)}
          {...attributes}
          {...listeners}
        >
          {page.blank ? (
            <div className="page-card__blank" aria-label="Blank page" />
          ) : thumb ? (
            <img
              src={thumb}
              className={page.rotation % 180 === 90 ? 'is-quarter-turned' : undefined}
              alt={`Page ${page.pageIndex + 1} of ${page.docName}`}
              style={{ transform: `rotate(${page.rotation}deg)` }}
              draggable={false}
            />
          ) : (
            <span className="page-card__placeholder">{failed ? 'Preview failed' : 'Loading…'}</span>
          )}
        </div>

        {/*
          Its own row above the controls: sharing a line with four buttons left
          room for about a dozen characters, which is not enough to tell two
          similarly named documents apart. Source page number leads, because
          that is what identifies the page — the badge on the thumbnail is its
          position in this canvas, which is a different number.
        */}
        <div
          className="page-card__source"
          title={page.blank ? 'Blank page' : `Page ${page.pageIndex + 1} of ${page.docName}`}
        >
          {!page.blank && <span className="page-card__page-no">{page.pageIndex + 1}</span>}
          <span className="page-card__doc-name">{page.blank ? 'Blank page' : page.docName}</span>
        </div>

        <div className="page-card__footer">
          <button
            className="icon-btn"
            onClick={() => onPreview(page.uid)}
            title="View larger"
            aria-label={`View page ${position} larger`}
          >
            <ExpandIcon />
          </button>
          <button
            className="icon-btn"
            disabled={page.blank}
            onClick={() => onRotate(page.uid, -90)}
            title="Rotate left"
            aria-label={`Rotate page ${position} left`}
          >
            <RotateLeftIcon />
          </button>
          <button
            className="icon-btn"
            disabled={page.blank}
            onClick={() => onRotate(page.uid, 90)}
            title="Rotate right"
            aria-label={`Rotate page ${position} right`}
          >
            <RotateRightIcon />
          </button>
          <button
            className={splitAfter ? 'icon-btn icon-btn--active' : 'icon-btn'}
            onClick={() => onToggleSplit(page.uid)}
            title={splitAfter ? 'Remove split after this page' : 'Split after this page'}
            aria-label={`Toggle split after page ${position}`}
            style={splitAfter ? { color: 'var(--accent)' } : undefined}
          >
            <ScissorsIcon />
          </button>
          <button
            className="icon-btn icon-btn--danger"
            onClick={() => onRemove(page.uid)}
            title="Delete page"
            aria-label={`Delete page ${position}`}
          >
            <TrashIcon />
          </button>
        </div>
      </div>

      {splitAfter && (
        <div className="split-slot">
          <div className="split-bar">New file starts here</div>
        </div>
      )}
    </>
  );
}

export const PageCard = memo(PageCardImpl);
